import { useMemo, useState } from 'react';
import { CalendarBlank, Clock, Plus, X } from '@phosphor-icons/react';
import { NotificationAlert } from '../../../shared/components/ui';

function getMondayWithOffset(offset) {
  const today = new Date();
  const day = today.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(today);
  monday.setDate(today.getDate() + diffToMonday + offset * 7);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

function formatDate(date) {
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
}

function toYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function NewPlanningModal({ open, onOpenChange, onSave, teams, isSaving }) {
  const [weeksCount, setWeeksCount] = useState(1);
  const [startOffset, setStartOffset] = useState(0);
  const [assignments, setAssignments] = useState({});
  const [error, setError] = useState('');

  const weeks = useMemo(() => {
    return Array.from({ length: weeksCount }, (_, i) => {
      const start = getMondayWithOffset(startOffset + i);
      const end = new Date(start);
      end.setDate(start.getDate() + 7);
      return {
        index: i,
        weekNumber: startOffset + i + 1,
        start,
        end,
      };
    });
  }, [weeksCount, startOffset]);

  const allAssigned = weeks.every((w) => assignments[w.index]);

  const removeWeek = (index) => {
    if (weeksCount <= 1) return;

    setWeeksCount((prev) => prev - 1);
    setAssignments((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        const idx = Number(k);
        if (idx < index) next[idx] = prev[idx];
        if (idx > index) next[idx - 1] = prev[idx];
      });
      return next;
    });
  };

  const close = () => {
    if (isSaving) return;
    onOpenChange(false);
  };

  const resetForm = () => {
    setWeeksCount(1);
    setStartOffset(0);
    setAssignments({});
    setError('');
  };

  const handleSave = async () => {
    setError('');

    if (!allAssigned) {
      setError('Debes seleccionar un equipo para cada semana.');
      return;
    }

    const payload = weeks.map((w) => ({
      weekNumber: w.weekNumber,
      startAt: toYmd(w.start),
      endAt: toYmd(w.end),
      teamId: assignments[w.index],
    }));

    try {
      await onSave(payload);
      resetForm();
      onOpenChange(false);
    } catch (err) {
      setError(err.message || 'No se pudo guardar la planificacion');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-[var(--app-overlay)] flex items-center justify-center z-[10020] p-4">
      <div className="w-full max-w-5xl bg-surface border border-line rounded-xl shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6">
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-content flex items-center gap-2">
              <CalendarBlank size={24} className="text-content" />
              Nueva Planificacion de Guardia
            </h3>
            <p className="text-content mt-2 text-sm md:text-lg">Asigna equipos de guardia para una o varias semanas consecutivas</p>
          </div>
          <button
            type="button"
            onClick={close}
            className="p-1 rounded text-content-muted hover:text-content hover:bg-surface-2"
            disabled={isSaving}
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-5">
          <NotificationAlert
            origin="guard-new-planning-modal"
            onClose={() => setError('')}
            notification={{
              show: Boolean(error),
              message: error,
              type: 'error',
              origin: 'guard-new-planning-modal',
            }}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm text-content mb-2">Semana de Inicio *</label>
              <select
                value={startOffset}
                onChange={(e) => setStartOffset(Number(e.target.value))}
                className="w-full h-12 px-4 rounded-lg bg-surface-2 border border-line text-content text-sm md:text-base"
                disabled={isSaving}
              >
                {Array.from({ length: 8 }, (_, i) => (
                  <option key={i} value={i}>
                    {`Semana ${10 + i} (${formatDate(getMondayWithOffset(i))})${i === 0 ? '  Actual' : ''}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-content mb-2">Cantidad de Semanas *</label>
              <select
                value={weeksCount}
                onChange={(e) => setWeeksCount(Number(e.target.value))}
                className="w-full h-12 px-4 rounded-lg bg-surface-2 border border-line text-content text-sm md:text-base"
                disabled={isSaving}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {`${n} semana${n > 1 ? 's' : ''}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <h4 className="text-lg md:text-xl font-semibold text-content">Asignacion por Semana</h4>
          </div>

          <div className="space-y-3 max-h-[42vh] overflow-y-auto pr-1">
            {weeks.map((week, idx) => {
              const isCurrent = startOffset + idx === 0;
              return (
                <div key={week.index} className={`rounded-xl p-4 border ${idx === 0 ? 'bg-surface-2 border-line' : 'bg-surface border-line'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center flex-wrap gap-2">
                      <p className="text-xl md:text-2xl font-bold text-content">Semana {10 + startOffset + idx}</p>
                      {isCurrent && <span className="text-success bg-success/12 px-2 py-0.5 rounded text-xs md:text-sm">Actual</span>}
                      <p className="text-content-muted text-sm md:text-base">Lun {formatDate(week.start)} 9:00 - Lun {formatDate(week.end)} 9:00</p>
                    </div>
                    {weeksCount > 1 && (
                      <button type="button" onClick={() => removeWeek(idx)} className="text-content-muted hover:text-content" disabled={isSaving}>
                        <X size={16} />
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-content-muted mb-2 text-sm">Equipo *</label>
                    <select
                      value={assignments[week.index] || ''}
                      onChange={(e) =>
                        setAssignments((prev) => ({
                          ...prev,
                          [week.index]: e.target.value,
                        }))
                      }
                      className="w-full md:w-72 h-12 px-4 rounded-lg bg-surface-2 border border-line text-content text-sm md:text-base"
                      disabled={isSaving}
                    >
                      <option value="">Seleccionar equipo</option>
                      {teams.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end -mt-2">
            <button
              type="button"
              disabled={weeksCount >= 5 || isSaving}
              onClick={() => setWeeksCount((p) => Math.min(5, p + 1))}
              className="inline-flex items-center gap-1 text-sm text-content hover:text-content-muted disabled:opacity-50"
            >
              <Plus size={14} />
              Agregar semana
            </button>
          </div>
        </div>

        <div className="p-6 pt-0 flex justify-end gap-3">
          <button
            type="button"
            onClick={close}
            className="px-6 py-2.5 rounded-xl bg-bg border border-line text-content text-sm md:text-base"
            disabled={isSaving}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!allAssigned || isSaving}
            className="px-8 py-2.5 rounded-xl bg-amber hover:bg-amber-hover text-on-amber text-sm md:text-base font-semibold disabled:opacity-50"
          >
            {isSaving ? 'Guardando...' : 'Crear Planificacion'}
          </button>
        </div>
      </div>
    </div>
  );
}
